import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { Observable, forkJoin, map, mergeMap, of } from 'rxjs';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { environment } from 'src/environments/environment';
import {
  AnalyticInteractionType,
  AnalyticPlatform,
  AnalyticSessionCreateInput,
  AnalyticSessionPageCreateInput,
} from 'src/types/graphql';
import { UserService } from '../user/user.service';
import {
  AnalyticSessionCreateGQL,
  AnalyticSessionKillGQL,
  AnalyticSessionPageCreateGQL,
  AnalyticSessionUpdateGQL,
} from './analytics.mutation.generated';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService extends BaseComponent {
  private url = 'https://pro.ip-api.com/json/';
  private queue: AnalyticSessionPageCreateInput[] = [];

  constructor(
    private analyticSessionCreateGQL: AnalyticSessionCreateGQL,
    private analyticSessionPageCreateGQL: AnalyticSessionPageCreateGQL,
    private analyticSessionKillGQL: AnalyticSessionKillGQL,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private analyticSessionUpdateGQL: AnalyticSessionUpdateGQL,
    private httpClient: HttpClient,
    private userService: UserService,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
  ) {
    super();
  }

  /**
   * Creates a new session and returns the session id as Observable
   *
   * @param url
   * @returns Observable<string>
   */
  init(): Observable<string> {
    return this.ngZone.runOutsideAngular(() => {
      if (this.isPlatformServer()) {
        return of(null);
      } else {
        return this.httpClient
          .get<IpResponse>(this.url, {
            observe: 'body',
            responseType: 'json',
            params: {
              key: environment.ipApiKey,
              fields: 'status,message,query,lat,lon',
            },
          })
          .pipe(
            map(res => {
              if (res.status === 'fail') {
                return {} as FallbackResponse;
              } else {
                return { ip: res.query, latitude: res.lat, longitude: res.lon };
              }
            }),
            mergeMap(res => {
              const session: AnalyticSessionCreateInput = {
                ipAddress: res.ip,
                latitude: res.latitude,
                longitude: res.longitude,
                noIp: !res.ip,
                logged: !!this.userService.user,
                platform: this.usingCapacitor ? AnalyticPlatform.App : AnalyticPlatform.Web,
                language: this.translateService.language,
                interactedAt: new Date().toISOString(),
                interactionType: AnalyticInteractionType.Session,
                origin: 'https://sharry.land',
              };
              return this.analyticSessionCreateGQL.mutate({ input: session });
            }),
            map(res => res.data.analyticSessionCreate?.id),
          );
      }
    });
  }

  /**
   * Add current page to session pages
   *
   * @param analyticSessionId
   * @param url
   */
  updateNavigation(analyticSessionId: string, url: string, firstAccessReferrer?: string) {
    this.ngZone.runOutsideAngular(() => {
      if (!analyticSessionId) {
        // SAVE for later
        this.queue.push({
          analyticSessionId: null,
          path: this.window.location?.href,
          normalizedPath: url,
          referrer: firstAccessReferrer || this.document.referrer,
        });
      } else {
        this.subscriptions.push(
          this.analyticSessionPageCreateGQL
            .mutate({
              input: {
                analyticSessionId,
                path: this.window.location?.href,
                normalizedPath: url,
                referrer: firstAccessReferrer || this.document.referrer,
              },
            })
            .subscribe(),
        );
        if (this.queue.length) {
          this.subscriptions.push(
            forkJoin(
              this.queue.map(input =>
                this.analyticSessionPageCreateGQL.mutate({
                  input: { ...input, analyticSessionId },
                }),
              ),
            ).subscribe(() => {
              this.queue = [];
            }),
          );
        }
      }
    });
  }

  updateUser(id: string, logged: boolean) {
    this.ngZone.runOutsideAngular(() => {
      this.subscriptions.push(
        this.analyticSessionUpdateGQL
          .mutate({
            input: {
              id,
              logged,
            },
          })
          .subscribe(),
      );
    });
  }

  /**
   * Kills the session
   *
   * @param id
   * @returns Observable<string>
   */
  close(id: string) {
    return this.ngZone.runOutsideAngular(() =>
      this.analyticSessionKillGQL
        .mutate({
          id,
        })
        .pipe(map(r => r.data.analyticSessionKill.id)),
    );
  }
}
export interface IpResponse {
  query: string;
  status: 'success' | 'fail';
  message?: 'private range' | 'reserved range' | 'invalid query';
  lat: number;
  lon: number;
}

export interface FallbackResponse {
  ip?: string;
  latitude?: number;
  longitude?: number;
}
